import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { BarChart3, TrendingUp, Users, CheckCircle2, AlertCircle, Clock, DollarSign, Eye, Sparkles } from "lucide-react";

function fmtNumber(v) {
  return Number(v || 0).toLocaleString();
}

function Stat({ testId, label, value, icon: Icon, accent = "text-foreground" }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-border/60 bg-background p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        {Icon ? <Icon size={14} className="text-muted-foreground" /> : null}
      </div>
      <div className={`mt-1 text-2xl font-black ${accent}`}>{value}</div>
    </div>
  );
}

export default function CampaignInsightsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get("/insights/campaigns");
        const list = Array.isArray(r.data) ? r.data : [];
        setCampaigns(list);
        if (list.length) setSelectedId(list[0].id);
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      setBusy(true);
      setError(null);
      try {
        const r = await api.get(`/insights/campaigns/${selectedId}/analytics`);
        setAnalytics(r.data);
      } catch (e) {
        setError(apiErrorMessage(e));
        setAnalytics(null);
      } finally {
        setBusy(false);
      }
    })();
  }, [selectedId]);

  const sel = useMemo(() => campaigns.find((c) => c.id === selectedId), [campaigns, selectedId]);

  if (loading) {
    return <div data-testid="insights-loading" className="py-12 text-center text-sm text-muted-foreground">Loading campaign insights...</div>;
  }

  if (!campaigns.length) {
    return (
      <div data-testid="insights-empty" className="mx-auto max-w-2xl px-3">
        <Card className="border-border/60 bg-card/80">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#c8ff00]/10 text-[#c8ff00]">
              <BarChart3 size={24} />
            </div>
            <div className="text-lg font-bold">No campaigns assigned yet</div>
            <div className="max-w-sm text-sm text-muted-foreground">
              An admin hasn't assigned you access to any campaign insights. Ask your contact at ClippingBase to grant access.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="campaign-insights-page" className="mx-auto max-w-6xl space-y-5 px-3 sm:px-0">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div data-testid="insights-title" className="text-2xl font-semibold">Campaign Insights</div>
          <div data-testid="insights-subtitle" className="text-sm text-muted-foreground">
            Performance analytics for the campaigns you've been granted access to.
          </div>
        </div>
        <div className="min-w-[240px]">
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger data-testid="insights-campaign-selector" className="border-border/60 bg-background">
              <SelectValue placeholder="Select a campaign" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div data-testid="insights-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>
      )}

      {sel && (
        <Card data-testid="insights-campaign-header" className="border-border/60 bg-card/80">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg">{sel.name}</CardTitle>
                <div className="mt-1 text-xs text-muted-foreground">{sel.creator_brand || "—"}</div>
              </div>
              <Badge className="bg-[#c8ff00]/10 capitalize text-[#c8ff00]">{sel.status}</Badge>
            </div>
          </CardHeader>
        </Card>
      )}

      {busy && !analytics && <div className="text-sm text-muted-foreground">Loading...</div>}

      {analytics && (
        <>
          {/* KPI grid */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat testId="insight-stat-views" label="Total views" value={fmtNumber(analytics.summary?.total_views)} icon={Eye} accent="text-[#c8ff00]" />
            <Stat testId="insight-stat-earned" label="Est. payout" value={`$${Number(analytics.summary?.total_earned || 0).toFixed(2)}`} icon={DollarSign} />
            <Stat testId="insight-stat-submissions" label="Submissions" value={fmtNumber(analytics.summary?.total_submissions)} icon={Sparkles} />
            <Stat testId="insight-stat-approval" label="Approval rate" value={`${Number(analytics.summary?.approval_rate || 0).toFixed(1)}%`} icon={CheckCircle2} />
            <Stat testId="insight-stat-members" label="Members joined" value={fmtNumber(analytics.summary?.members)} icon={Users} />
            <Stat testId="insight-stat-accepted" label="Accepted" value={fmtNumber(analytics.summary?.accepted)} icon={CheckCircle2} />
            <Stat testId="insight-stat-pending" label="Pending" value={fmtNumber(analytics.summary?.pending)} icon={Clock} />
            <Stat testId="insight-stat-flagged" label="Rejected" value={fmtNumber(analytics.summary?.flagged)} icon={AlertCircle} />
          </div>

          {/* Trend chart */}
          <Card data-testid="insight-trend-card" className="border-border/60 bg-card/80">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <TrendingUp size={14} className="text-[#c8ff00]" />
              <CardTitle className="text-sm">Views over time (last 30 days)</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.trend?.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={analytics.trend} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="insightsViewsTrend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#c8ff00" stopOpacity={0.45} />
                        <stop offset="95%" stopColor="#c8ff00" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))", fontSize: 12 }} />
                    <Area type="monotone" dataKey="views" stroke="#c8ff00" strokeWidth={2.5} fill="url(#insightsViewsTrend)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[180px] items-center justify-center rounded-xl border border-dashed border-border/50 text-xs text-muted-foreground">No trend data yet</div>
              )}
            </CardContent>
          </Card>

          {/* Top performers */}
          <Card data-testid="insight-top-performers-card" className="border-border/60 bg-card/80">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <Users size={14} className="text-[#c8ff00]" />
              <CardTitle className="text-sm">Top performing clippers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {analytics.top_performers?.length ? (
                analytics.top_performers.map((p, i) => (
                  <div key={p.user_id || i} data-testid={`insight-top-performer-${i}`} className="flex items-center gap-3 rounded-xl border border-border/60 bg-background p-3">
                    <div className="w-8 text-center text-xs font-black text-[#c8ff00]">#{i + 1}</div>
                    <img alt="" src={p.avatar_url || `https://api.dicebear.com/8.x/initials/svg?seed=${p.username}`} className="h-9 w-9 rounded-full" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-foreground">@{p.username}</div>
                      <div className="text-xs text-muted-foreground">{p.clips} clip{p.clips === 1 ? "" : "s"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold tabular-nums">{fmtNumber(p.views)} views</div>
                      <div className="text-xs font-bold text-[#c8ff00]">${Number(p.earned).toFixed(2)}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-6 text-center text-sm text-muted-foreground">No approved clips yet.</div>
              )}
            </CardContent>
          </Card>

          {/* Accepted clips list */}
          <Card data-testid="insight-accepted-clips-card" className="border-border/60 bg-card/80">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-400" />
              <CardTitle className="text-sm">Accepted clips ({analytics.accepted_clips?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {analytics.accepted_clips?.length ? (
                analytics.accepted_clips.map((c, i) => (
                  <div key={c.id || i} data-testid={`insight-accepted-clip-${i}`} className="flex items-center gap-3 rounded-xl border border-emerald-500/15 bg-emerald-500/[0.03] p-3">
                    <img alt="" src={c.avatar_url || `https://api.dicebear.com/8.x/initials/svg?seed=${c.username}`} className="h-9 w-9 rounded-full" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-foreground">@{c.username} <span className="ml-1 text-[10px] uppercase text-muted-foreground">· {c.platform || "—"}</span></div>
                      <div className="text-xs text-muted-foreground tabular-nums">{fmtNumber(c.views)} views</div>
                    </div>
                    <a data-testid={`insight-accepted-clip-link-${i}`} href={c.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1.5 text-xs font-bold text-emerald-300 hover:bg-emerald-500/25">
                      View clip
                    </a>
                  </div>
                ))
              ) : (
                <div className="py-6 text-center text-sm text-muted-foreground">No accepted clips yet.</div>
              )}
            </CardContent>
          </Card>

          {/* Rejected clips list */}
          <Card data-testid="insight-rejected-clips-card" className="border-border/60 bg-card/80">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <AlertCircle size={14} className="text-rose-400" />
              <CardTitle className="text-sm">Rejected clips ({analytics.rejected_clips?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {analytics.rejected_clips?.length ? (
                analytics.rejected_clips.map((c, i) => (
                  <div key={c.id || i} data-testid={`insight-rejected-clip-${i}`} className="flex items-start gap-3 rounded-xl border border-rose-500/15 bg-rose-500/[0.03] p-3">
                    <img alt="" src={c.avatar_url || `https://api.dicebear.com/8.x/initials/svg?seed=${c.username}`} className="h-9 w-9 shrink-0 rounded-full" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-foreground">@{c.username} <span className="ml-1 text-[10px] uppercase text-muted-foreground">· {c.platform || "—"}</span></div>
                      {c.rejection_reason ? (
                        <div className="mt-0.5 line-clamp-2 text-[11px] text-rose-300">Reason: {c.rejection_reason}</div>
                      ) : (
                        <div className="mt-0.5 text-[11px] text-muted-foreground">No reason provided.</div>
                      )}
                    </div>
                    <a data-testid={`insight-rejected-clip-link-${i}`} href={c.video_url} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-3 py-1.5 text-xs font-bold text-rose-300 hover:bg-rose-500/25">
                      View clip
                    </a>
                  </div>
                ))
              ) : (
                <div className="py-6 text-center text-sm text-muted-foreground">No rejected clips.</div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
